import React from 'react';
import { Input, Button } from '@heroui/react';
import { Search, X, Filter, Building2 } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useDepartments } from '@/hooks/queries/useDepartments';
import DepartmentSelector from '@/components/base/selectors/DepartmentSelector';

interface PeopleFiltersBarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedDepartmentId?: string;
  onDepartmentChange: (departmentId: string | undefined) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
}

const PeopleFiltersBar: React.FC<PeopleFiltersBarProps> = ({
  searchQuery,
  onSearchChange,
  selectedDepartmentId,
  onDepartmentChange,
  hasActiveFilters,
  onClearFilters,
}) => {
  const { t } = useTranslation('people');
  const { data: departments, isLoading: isLoadingDepartments } = useDepartments();

  return (
    <div className="relative">
      {/* Glassmorphism background */}
      <div className="absolute inset-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-800/50 shadow-lg" />

      <div className="relative p-6 md:p-8 mb-8">
        {/* Header */}
        <div className="flex items-center gap-2 mb-6">
          <div className="p-2 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600">
            <Filter className="w-4 h-4 text-white" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t('filters.title')}</h2>
        </div>

        <div className="space-y-5">
          {/* Search with modern styling */}
          <div className="w-full">
            <Input
              placeholder={t('filters.searchPlaceholder')}
              value={searchQuery}
              onValueChange={onSearchChange}
              startContent={
                <div className="p-1 rounded-md bg-gray-100 dark:bg-gray-800">
                  <Search className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </div>
              }
              isClearable
              onClear={() => onSearchChange('')}
              className="w-full"
              variant="bordered"
              size="lg"
              classNames={{
                input: 'text-base',
                inputWrapper:
                  'border-gray-300 dark:border-gray-700 hover:border-indigo-400 dark:hover:border-indigo-600 transition-colors',
              }}
            />
          </div>

          {/* Filters Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Department Filter */}
            <div className="sm:col-span-1">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  <span>{t('filters.department')}</span>
                </div>
              </label>
              <DepartmentSelector
                departments={departments || []}
                isLoading={isLoadingDepartments}
                value={selectedDepartmentId || ''}
                onChange={(value) => {
                  onDepartmentChange(value === '' ? undefined : value);
                }}
                placeholder={t('filters.allDepartments')}
                label=""
                variant="bordered"
                className="w-full"
                showEmptyOption={true}
              />
            </div>

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <div className="sm:col-span-1 flex items-end">
                <Button
                  variant="flat"
                  color="danger"
                  onPress={onClearFilters}
                  startContent={<X className="w-4 h-4" />}
                  className="w-full sm:w-auto whitespace-nowrap font-semibold mt-6"
                  size="lg"
                >
                  {t('filters.clearAll')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PeopleFiltersBar;
